import React, { useEffect, useState } from "react"
import { MDBDataTable } from "mdbreact"
import {
    Row, Col, Card, CardBody, CardTitle, Badge,
    Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input
} from "reactstrap"
import axios from "../../utils/axios"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { getProfileImageUrl } from "../../helpers/image_helpers"
import "./datatables.scss"
import Swal from "sweetalert2"

const Payments = (props) => {
    const [payments, setPayments] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const [formData, setFormData] = useState({
        user_id: "",
        related_type_id: "",
        related_type: "",
        note: "",
        payment_amount: "",
        payment_currency: "USD",
        payment_gateway: "",
        payment_transaction_id: "",
        payment_status: "pending",
    })
    const [editingId, setEditingId] = useState(null)

    const breadcrumbItems = [
        { title: "Payments", link: "#" },
        { title: "All Payments", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems('Payments', breadcrumbItems)
        fetchPayments()
    }, [])

    const fetchPayments = async () => {
        setLoading(true)
        try {
            const res = await axios.get("/api/payments")
            setPayments(res.data?.data)
        } catch (error) {
            setError(error.message || "Error fetching payments")
        } finally {
            setLoading(false)
        }
    }

    const toggleModal = () => {
        setModalOpen(!modalOpen)
        if (!modalOpen) resetForm()
    }

    const resetForm = () => {
        setFormData({
            user_id: "",
            related_type_id: "",
            related_type: "",
            note: "",
            payment_amount: "",
            payment_currency: "USD",
            payment_gateway: "",
            payment_transaction_id: "",
            payment_status: "pending",
        })
        setIsEdit(false)
        setEditingId(null)
    }

    const handleInputChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value })
    }

    const handleSubmit = async e => {
        e.preventDefault()
        try {
            if (isEdit) {
                const res = await axios.put(`/api/payments/${editingId}`, formData)
                // setPayments(payments?.data?.map(p => p.id === editingId ? res.data?.data : p))
                fetchPayments()
            } else {
                const res = await axios.post("/api/payments", formData)
                // setPayments([...payments?.data, res.data])
                fetchPayments()
            }
            toggleModal()
        } catch (err) {
            alert(err.response?.data?.message || "Error saving payment")
        }
    }

    const handleEdit = payment => {
        setIsEdit(true)
        setEditingId(payment.id)
        setFormData({
            user_id: payment.user_id,
            related_type_id: payment.related_type_id,
            related_type: payment.related_type,
            note: payment.note,
            payment_amount: payment.payment_amount,
            payment_currency: payment.payment_currency,
            payment_gateway: payment.payment_gateway,
            payment_transaction_id: payment.payment_transaction_id,
            payment_status: payment.payment_status || 'pending',
        })
        setModalOpen(!modalOpen);
    }

    const deletePayment = async id => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`/api/payments/${id}`)
                    fetchPayments()
                } catch (err) {
                    alert(err.response?.data?.message || "Error deleting payment")
                }
            }
        })
    }

    const getStatusBadge = (status) => {
        const map = {
            completed: "bg-success",
            pending: "bg-warning",
            failed: "bg-danger",
            refunded: "bg-info"
        }
        return <Badge className={`badge ${map[status] || "bg-secondary"}`}>{status}</Badge>
    }

    const data = {
        columns: [
            { label: "ID", field: "id", sort: "asc", width: 50 },
            { label: "User", field: "user", sort: "asc", width: 200 },
            { label: "Amount", field: "amount", sort: "asc", width: 100 },
            { label: "Status", field: "status", sort: "asc", width: 100 },
            { label: "Gateway", field: "payment_gateway", sort: "asc", width: 120 },
            { label: "Transaction ID", field: "payment_transaction_id", sort: "asc", width: 180 },
            { label: "Actions", field: "actions", sort: "disabled", width: 150 }
        ],
        rows: payments?.data?.map(payment => ({
            id: payment.id,
            user: payment.user?.name || `User#${payment.user_id}`,
            amount: `$${(payment.payment_amount / 100).toFixed(2)}`,
            status: getStatusBadge(payment.payment_status),
            payment_gateway: payment.payment_gateway,
            payment_transaction_id: payment.payment_transaction_id,
            actions: (
                <div>
                    <Button size="sm" color="info" onClick={() => handleEdit(payment)} className="me-2">Edit</Button>
                    <Button size="sm" color="danger" onClick={() => deletePayment(payment.id)}>Delete</Button>
                </div>
            )
        }))
    }

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card>
                        <CardBody>
                            <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                Payment Management
                                <Button color="primary" onClick={toggleModal}>+ Add Payment</Button>
                            </CardTitle>
                            <p className="card-title-desc">Manage payment transactions</p>

                            {loading ? (
                                <div className="text-center p-4">Loading...</div>
                            ) : error ? (
                                <div className="alert alert-danger">{error}</div>
                            ) : (
                                <MDBDataTable
                                    responsive
                                    bordered
                                    data={data}
                                    className="mt-4"
                                    noBottomColumns
                                    striped
                                    hover
                                />
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={modalOpen} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>{isEdit ? "Edit Payment" : "Add Payment"}</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label>User ID</Label>
                            <Input type="number" name="user_id" value={formData.user_id} onChange={handleInputChange} required />
                        </FormGroup>
                        <FormGroup>
                            <Label>Related Type</Label>
                            <Input name="related_type" value={formData.related_type} onChange={handleInputChange} required />
                        </FormGroup>
                        <FormGroup>
                            <Label>Related Type ID</Label>
                            <Input type="number" name="related_type_id" value={formData.related_type_id} onChange={handleInputChange} required />
                        </FormGroup>
                        <FormGroup>
                            <Label>Note</Label>
                            <Input name="note" value={formData.note} onChange={handleInputChange} />
                        </FormGroup>
                        <FormGroup>
                            <Label>Amount (in cents)</Label>
                            <Input type="number" name="payment_amount" value={formData.payment_amount} onChange={handleInputChange} required />
                        </FormGroup>
                        <FormGroup>
                            <Label>Currency</Label>
                            <Input name="payment_currency" value={formData.payment_currency} onChange={handleInputChange} />
                        </FormGroup>
                        <FormGroup>
                            <Label>Gateway</Label>
                            <Input name="payment_gateway" value={formData.payment_gateway} onChange={handleInputChange} required />
                        </FormGroup>
                        <FormGroup>
                            <Label>Transaction ID</Label>
                            <Input name="payment_transaction_id" value={formData.payment_transaction_id} onChange={handleInputChange} required />
                        </FormGroup>
                        <FormGroup>
                            <Label>Payment Status</Label>
                            <Input 
                                type="select" 
                                name="payment_status" 
                                value={formData.payment_status} 
                                onChange={handleInputChange}
                                required
                            >
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                                <option value="failed">Failed</option>
                                <option value="refunded">Refunded</option>
                            </Input>
                        </FormGroup>
                        <ModalFooter>
                            <Button type="submit" color="primary">{isEdit ? "Update" : "Create"}</Button>
                            <Button color="secondary" onClick={toggleModal}>Cancel</Button>
                        </ModalFooter>
                    </Form>
                </ModalBody>
            </Modal>
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(Payments)
